/**
 * Fantasy Console 0 - Cart Service
 *
 * Handles all cart operations with Supabase cloud storage.
 * Supports authenticated saves, likes, and forks.
 */

import {
  supabase,
  isSupabaseConfigured,
  DbCart,
  DbCartWithStats,
} from '../lib/supabase'
import type { Cart, CodeFile } from '@fc0/types'

/**
 * Extended Cart type with stats from database view
 */
export interface CartWithStats extends Cart {
  username?: string
  likeCount: number
  isLikedByCurrentUser: boolean
}

/**
 * Result from saving a cart
 */
export interface SaveCartResult {
  id: string
  updatedAt: string
}

/**
 * Paginated result type
 */
export interface PaginatedResult<T> {
  data: T[]
  hasMore: boolean
  total: number
}

/**
 * Query options for fetching carts
 */
export interface CartQueryOptions {
  sortBy?: 'updated' | 'created' | 'title' | 'likes'
  limit?: number
  offset?: number
  search?: string
}

const DEFAULT_PAGE_SIZE = 20

/**
 * Cart service for Supabase operations
 */
export const CartService = {
  /**
   * Save cart to Supabase (requires authentication)
   * @param cart The cart to save
   * @returns The cart ID and updated_at timestamp
   */
  async saveCart(cart: Cart): Promise<SaveCartResult> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    // Check authentication
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('You must be signed in to save to cloud')
    }

    const cartId = cart.id || crypto.randomUUID()

    // Prepare data for database
    const dbCart: Omit<DbCart, 'created_at' | 'updated_at'> = {
      id: cartId,
      version: cart.version ?? null,
      user_id: user.id,
      title: cart.metadata.title,
      author: cart.metadata.author || null,
      description: cart.metadata.description || null,
      code_files: cart.codeFiles,
      sprites: cart.sprites,
      map: cart.map,
      sfx: cart.sfx ?? null,
      thumbnail_url: cart.thumbnailUrl || null,
      forked_from: null,
    }

    // Upsert cart (insert or update)
    const { error } = await supabase
      .from('carts')
      .upsert(dbCart, { onConflict: 'id' })

    if (error) {
      console.error('Failed to save cart:', error)
      throw new Error(`Failed to save cart: ${error.message}`)
    }

    // Fetch the saved record to get updated_at
    const { data: savedCart } = await supabase
      .from('carts')
      .select('id, updated_at')
      .eq('id', cartId)
      .single()

    return {
      id: cartId,
      updatedAt: savedCart?.updated_at || new Date().toISOString(),
    }
  },

  /**
   * Load cart from Supabase
   * @param cartId The cart ID to load
   * @returns The loaded cart
   */
  async loadCart(cartId: string): Promise<Cart> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    const { data, error } = await supabase
      .from('carts')
      .select('*')
      .eq('id', cartId)
      .single()

    if (error) {
      console.error('Failed to load cart:', error)
      throw new Error(`Failed to load cart: ${error.message}`)
    }

    if (!data) {
      throw new Error('Cart not found')
    }

    return this.dbCartToCart(data)
  },

  /**
   * Load a single cart with username from Supabase (for share/play page)
   * Uses the carts_with_stats view to get username and like counts
   * @param cartId The cart ID to load
   * @returns The loaded cart with stats
   */
  async loadCartWithUsername(cartId: string): Promise<CartWithStats> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    const { data, error } = await supabase
      .from('carts_with_stats')
      .select('*')
      .eq('id', cartId)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        throw new Error('Cart not found')
      }
      console.error('Failed to load cart:', error)
      throw new Error(`Failed to load cart: ${error.message}`)
    }

    if (!data) {
      throw new Error('Cart not found')
    }

    return this.dbCartWithStatsToCart(data)
  },

  /**
   * Get all carts from Supabase with stats (for library view)
   * @param options Query options for pagination and sorting
   * @returns Paginated array of carts with like counts and user info
   */
  async getAllCarts(
    options: CartQueryOptions = {},
  ): Promise<PaginatedResult<CartWithStats>> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    const {
      sortBy = 'updated',
      limit = DEFAULT_PAGE_SIZE,
      offset = 0,
    } = options

    // Build the query
    let query = supabase
      .from('carts_with_stats')
      .select('*', { count: 'exact' })

    // Apply sorting
    switch (sortBy) {
      case 'created':
        query = query.order('created_at', { ascending: false })
        break
      case 'title':
        query = query.order('title', { ascending: true })
        break
      case 'likes':
        query = query.order('like_count', { ascending: false })
        break
      case 'updated':
      default:
        query = query.order('updated_at', { ascending: false })
        break
    }

    // Apply pagination
    query = query.range(offset, offset + limit - 1)

    const { data, error, count } = await query

    if (error) {
      console.error('Failed to load carts:', error)
      throw new Error(`Failed to load carts: ${error.message}`)
    }

    const total = count || 0
    const carts = (data || []).map((row) => this.dbCartWithStatsToCart(row))

    return {
      data: carts,
      hasMore: offset + carts.length < total,
      total,
    }
  },

  /**
   * Get only carts belonging to the current user
   * @param options Query options for pagination and sorting
   * @returns Paginated array of user's carts with stats
   */
  async getMyCartsOnly(
    options: CartQueryOptions = {},
  ): Promise<PaginatedResult<CartWithStats>> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('You must be signed in to view your carts')
    }

    const {
      sortBy = 'updated',
      limit = DEFAULT_PAGE_SIZE,
      offset = 0,
    } = options

    // Build the query
    let query = supabase
      .from('carts_with_stats')
      .select('*', { count: 'exact' })
      .eq('user_id', user.id)

    // Apply sorting
    switch (sortBy) {
      case 'created':
        query = query.order('created_at', { ascending: false })
        break
      case 'title':
        query = query.order('title', { ascending: true })
        break
      case 'likes':
        query = query.order('like_count', { ascending: false })
        break
      case 'updated':
      default:
        query = query.order('updated_at', { ascending: false })
        break
    }

    // Apply pagination
    query = query.range(offset, offset + limit - 1)

    const { data, error, count } = await query

    if (error) {
      console.error('Failed to load carts:', error)
      throw new Error(`Failed to load carts: ${error.message}`)
    }

    const total = count || 0
    const carts = (data || []).map((row) => this.dbCartWithStatsToCart(row))

    return {
      data: carts,
      hasMore: offset + carts.length < total,
      total,
    }
  },

  /**
   * Delete cart from Supabase
   * @param cartId The cart ID to delete
   */
  async deleteCart(cartId: string): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    // Get cart to check for thumbnail
    const { data: cart } = await supabase
      .from('carts')
      .select('thumbnail_url')
      .eq('id', cartId)
      .single()

    // Delete thumbnail from storage if exists
    if (cart?.thumbnail_url) {
      const fileName = cart.thumbnail_url.split('/').pop()
      if (fileName) {
        await supabase.storage.from('cart-thumbnails').remove([fileName])
      }
    }

    // Delete cart from database
    const { error } = await supabase.from('carts').delete().eq('id', cartId)

    if (error) {
      console.error('Failed to delete cart:', error)
      throw new Error(`Failed to delete cart: ${error.message}`)
    }
  },

  /**
   * Duplicate a cart (creates a copy owned by current user)
   * @param cartId The cart ID to duplicate
   * @returns The new cart ID
   */
  async duplicateCart(cartId: string): Promise<string> {
    const cart = await this.loadCart(cartId)

    // Create new cart with modified title
    const newCart: Cart = {
      ...cart,
      id: undefined,
      metadata: {
        ...cart.metadata,
        title: `${cart.metadata.title} (Copy)`,
      },
      thumbnailUrl: undefined,
      createdAt: undefined,
      updatedAt: undefined,
    }

    const result = await this.saveCart(newCart)
    return result.id
  },

  /**
   * Fork a cart (creates a copy with reference to original)
   * @param cartId The cart ID to fork
   * @returns The new cart ID
   */
  async forkCart(cartId: string): Promise<string> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    // Check authentication
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('You must be signed in to fork a cart')
    }

    // Load the original cart
    const originalCart = await this.loadCart(cartId)
    const newCartId = crypto.randomUUID()

    // Prepare the forked cart
    const dbCart: Omit<DbCart, 'created_at' | 'updated_at'> = {
      id: newCartId,
      version: originalCart.version ?? null,
      user_id: user.id,
      title: `${originalCart.metadata.title} (Fork)`,
      author: originalCart.metadata.author || null,
      description: originalCart.metadata.description || null,
      code_files: originalCart.codeFiles,
      sprites: originalCart.sprites,
      map: originalCart.map,
      sfx: originalCart.sfx ?? null,
      thumbnail_url: null, // Will be generated on next save
      forked_from: cartId,
    }

    const { error } = await supabase.from('carts').insert(dbCart)

    if (error) {
      console.error('Failed to fork cart:', error)
      throw new Error(`Failed to fork cart: ${error.message}`)
    }

    return newCartId
  },

  /**
   * Update cart metadata only
   * @param cartId The cart ID to update
   * @param metadata The metadata fields to update
   */
  async updateMetadata(
    cartId: string,
    metadata: { title?: string; author?: string; description?: string },
  ): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    const { error } = await supabase
      .from('carts')
      .update(metadata)
      .eq('id', cartId)

    if (error) {
      console.error('Failed to update cart metadata:', error)
      throw new Error(`Failed to update cart metadata: ${error.message}`)
    }
  },

  /**
   * Update cart thumbnail URL
   * @param cartId The cart ID to update
   * @param thumbnailUrl The new thumbnail URL
   */
  async updateThumbnailUrl(
    cartId: string,
    thumbnailUrl: string,
  ): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    const { error } = await supabase
      .from('carts')
      .update({ thumbnail_url: thumbnailUrl })
      .eq('id', cartId)

    if (error) {
      console.error('Failed to update thumbnail URL:', error)
      throw new Error(`Failed to update thumbnail URL: ${error.message}`)
    }
  },

  /**
   * Convert database cart row to cart
   */
  dbCartToCart(dbCart: DbCart): Cart {
    return {
      id: dbCart.id,
      version: dbCart.version ?? undefined,
      userId: dbCart.user_id,
      metadata: {
        title: dbCart.title,
        author: dbCart.author || '',
        description: dbCart.description || '',
      },
      codeFiles: dbCart.code_files as CodeFile[],
      sprites: dbCart.sprites,
      map: dbCart.map,
      sfx: dbCart.sfx,
      forkedFrom: dbCart.forked_from,
      thumbnailUrl: dbCart.thumbnail_url || undefined,
      createdAt: dbCart.created_at,
      updatedAt: dbCart.updated_at,
    }
  },

  /**
   * Convert database cart with stats to CartWithStats
   */
  dbCartWithStatsToCart(dbCart: DbCartWithStats): CartWithStats {
    return {
      ...this.dbCartToCart(dbCart),
      username: dbCart.username || undefined,
      likeCount: dbCart.like_count || 0,
      isLikedByCurrentUser: dbCart.is_liked_by_current_user || false,
      userId: dbCart.user_id || undefined,
      forkedFrom: dbCart.forked_from || undefined,
    }
  },
}
